import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { prProfileUrl } from "views_shared/app/javascript/utils/urls";
import useIsMobile from "../hooks/useIsMobile";
import TabBar from "./TabBar";
import ExpertsSearch from "../experts/search/index";
import CompaniesSearch from "../companies/search/index";
import PrProfiles from "../pr_profiles/PrProfiles";
import ProductSearch from "../products/ProductSearch";
import StoryIdeasSearch from "../story_ideas/StoryIdeasSearch";
import MediaProfilesSearch from "../media_profiles/MediaProfilesSearch";
import MediaOutletsSearch from "../media_outlets/MediaOutletsSearch";
import sharedAlgoliaRouter from "./sharedAlgoliaRouter";
import GroupDeleteConfirm from "./GroupDeleteConfirm";
import SavedGroupsLoadError from "./SavedGroupsLoadError";
import { initials } from "./utils";
import { tabFromPathname, tabPath, applyTabTitle } from "./tabRouting";
import {
  SavedGroupsProvider,
  useSavedGroupsState,
  useSavedGroupsActions,
} from "./SavedGroupsContext/SavedGroupsContext";
import { findGroupByName, isOptimisticId } from "./SavedGroupsContext/useSavedGroups";
import { LastMessagesProvider } from "./LastMessagesContext/LastMessagesContext";

const SOURCE_TYPE_LABEL = {
  "Id::PromoEntity::Expert": "Expert",
  "Id::PromoEntity::Company": "Company",
  "Id::PromoEntity::Product": "Product",
  "Id::Profile::Promoter": "PR",
  "Id::Profile::MediaProfile": "Media",
  "Id::MediaOutlet": "Media Outlet",
  "Enrich::PublicPitch": "Story Idea",
};

const SOURCE_TYPE_PATH = {
  "Id::PromoEntity::Expert": "experts",
  "Id::PromoEntity::Company": "companies",
  "Id::PromoEntity::Product": "products",
  "Id::Profile::Promoter": "pr_profiles",
  "Id::Profile::MediaProfile": "media_profiles",
  "Id::MediaOutlet": "media_outlets",
};

function memberProfilePath(type, slug) {
  const prefix = SOURCE_TYPE_PATH[type];
  return prefix && slug ? `/${prefix}/${slug}` : null;
}

function membersForGroup(membershipIndex, groupId) {
  return Object.entries(membershipIndex).flatMap(([type, byId]) =>
    Object.entries(byId).flatMap(([sid, memberships]) =>
      memberships
        .filter((m) => String(m.saved_group_id) === String(groupId))
        .map((m) => ({
          type,
          sourceableId: sid,
          membershipId: m.id,
          name: m.name,
          authorName: m.authorName,
          authorSlug: m.authorSlug,
          authorMigrateId: m.authorMigrateId,
          imageUrl: m.imageUrl,
          slug: m.slug,
        })),
    ),
  );
}

function MemberAvatar({ name, imageUrl, type }) {
  const [imgError, setImgError] = useState(false);
  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        onError={() => setImgError(true)}
        style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  // Story ideas have no headshot: mark them with a story icon rather than initials, which read
  // as a person.
  const isStoryIdea = type === "Enrich::PublicPitch";
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        background: isStoryIdea ? "#ede9fe" : "#e5e7eb",
        color: isStoryIdea ? "#533dd1" : "#6b7280",
        fontSize: 9,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {isStoryIdea ? <i className="fa-regular fa-newspaper" style={{ fontSize: 11 }} /> : initials(name)}
    </div>
  );
}

MemberAvatar.propTypes = {
  name: PropTypes.string,
  imageUrl: PropTypes.string,
  type: PropTypes.string,
};

function GroupIcon() {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: "#ede9fe",
        color: "#533dd1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
      </svg>
    </div>
  );
}

function GroupRowContent({
  group,
  confirmDeleteId,
  editingId,
  editName,
  editInputRef,
  isExpanded,
  onDeleteRequest,
  onConfirmDelete,
  onCancelDelete,
  onStartEdit,
  onEditChange,
  onEditBlur,
  onEditKeyDown,
}) {
  if (confirmDeleteId === group.id) {
    return <GroupDeleteConfirm group={group} onConfirm={onConfirmDelete} onCancel={onCancelDelete} />;
  }

  const nameContent =
    editingId === group.id ? (
      <input
        ref={editInputRef}
        type="text"
        value={editName}
        onChange={onEditChange}
        onBlur={onEditBlur}
        onKeyDown={onEditKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          border: "1px solid #533dd1",
          borderRadius: 6,
          padding: "3px 7px",
          fontSize: 14,
          fontWeight: 500,
          outline: "none",
          minWidth: 0,
        }}
      />
    ) : (
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {group.name}
      </span>
    );

  return (
    <>
      <GroupIcon />
      {nameContent}
      <span className="text-primary-60" style={{ fontSize: 12, flexShrink: 0 }}>
        {group.member_count || 0} entries {isExpanded ? "▾" : "▸"}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStartEdit(group);
        }}
        title="Rename group"
        className="text-primary-60"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDeleteRequest();
        }}
        title="Delete group"
        className="text-destructive-80"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
          <path d="M9 6V4h6v2" />
        </svg>
      </button>
    </>
  );
}

GroupRowContent.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    member_count: PropTypes.number,
  }).isRequired,
  confirmDeleteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editName: PropTypes.string.isRequired,
  editInputRef: PropTypes.shape({}).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onDeleteRequest: PropTypes.func.isRequired,
  onConfirmDelete: PropTypes.func.isRequired,
  onCancelDelete: PropTypes.func.isRequired,
  onStartEdit: PropTypes.func.isRequired,
  onEditChange: PropTypes.func.isRequired,
  onEditBlur: PropTypes.func.isRequired,
  onEditKeyDown: PropTypes.func.isRequired,
};

export function MyGroupsPanel({ onClose }) {
  const state = useSavedGroupsState();
  const actions = useSavedGroupsActions();
  const [newName, setNewName] = useState("");
  const [createInputFocused, setCreateInputFocused] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const creatingRef = useRef(false);
  const editInputRef = useRef(null);
  const isMobile = useIsMobile();
  const groups = useMemo(() => state?.groups || [], [state?.groups]);
  const membershipIndex = state?.membershipIndex || {};

  useEffect(() => {
    if (editingId !== null) editInputRef.current?.focus();
  }, [editingId]);

  useEffect(() => {
    if (!isMobile) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isMobile, onClose]);

  const startEdit = useCallback((group) => {
    setEditingId(group.id);
    setEditName(group.name);
    setConfirmDeleteId(null);
  }, []);

  const commitEdit = useCallback(async () => {
    const trimmed = editName.trim();
    setEditingId(null);
    if (!trimmed || trimmed === groups.find((g) => g.id === editingId)?.name) return;
    await actions.updateGroup(editingId, trimmed);
  }, [editName, editingId, groups, actions]);

  const handleEditKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") commitEdit();
      if (e.key === "Escape") setEditingId(null);
    },
    [commitEdit],
  );

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || creatingRef.current) return;

    const existing = findGroupByName(groups, trimmed);
    if (existing) {
      globalThis.Qwoted?.showToast?.({ toastTitle: `You already have a group named "${existing.name}"` });
      setNewName("");
      return;
    }

    creatingRef.current = true;
    setIsCreating(true);
    try {
      if (await actions.createGroup(trimmed)) setNewName("");
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
    }
  };

  const sidePad = isMobile ? 16 : 24;

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="My Groups"
      style={
        isMobile
          ? {
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1051,
              background: "#fff",
              borderRadius: "16px 16px 0 0",
              boxShadow: "0 -4px 24px rgba(10,13,18,0.18)",
              maxHeight: "85vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }
          : {
              position: "fixed",
              top: 80,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1001,
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              width: 600,
              maxHeight: "70vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }
      }
    >
      {isMobile && (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "#d2d3d4" }} />
        </div>
      )}
      {/* Header */}
      <div
        style={{
          padding: isMobile ? `8px ${sidePad}px 16px` : "20px 24px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 18 }}>My Groups</span>
        {isMobile ? (
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "none",
              color: "#533dd1",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              padding: 4,
            }}
          >
            Done
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#9ca3af",
              padding: 0,
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Create section — top */}
      <div style={{ padding: `0 ${sidePad}px 16px`, borderBottom: "1px solid #f0f0f0", display: "flex", gap: 10 }}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          onFocus={() => setCreateInputFocused(true)}
          onBlur={() => setCreateInputFocused(false)}
          placeholder="Name a new group (e.g. Women in Tech, AI Experts, Healthtech)"
          style={{
            flex: 1,
            border: `1px solid ${createInputFocused ? "#533dd1" : "#d1d5db"}`,
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 14,
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={!newName.trim() || isCreating}
          style={{
            background: newName.trim() ? "#4b5563" : "#9ca3af",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 20px",
            fontSize: 14,
            fontWeight: 600,
            cursor: newName.trim() ? "pointer" : "not-allowed",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          + Create
        </button>
      </div>

      {/* Groups list */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {state?.error && (
          <SavedGroupsLoadError error={state.error} onRetry={actions.reload} style={{ padding: `12px ${sidePad}px` }} />
        )}
        {groups.length === 0 && !state?.error && (
          <div style={{ padding: `32px ${sidePad}px`, color: "#9ca3af", fontSize: 14, textAlign: "center" }}>
            No groups yet. Create one above to start organizing your entries.
          </div>
        )}
        {groups.map((group) => {
          const isExpanded = expandedGroupId === group.id;
          const members = isExpanded ? membersForGroup(membershipIndex, group.id) : [];

          return (
            <div key={group.id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (editingId !== group.id && confirmDeleteId !== group.id) {
                    setExpandedGroupId(isExpanded ? null : group.id);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.target.tagName === "INPUT") return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (editingId !== group.id && confirmDeleteId !== group.id) {
                      setExpandedGroupId(isExpanded ? null : group.id);
                    }
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: `12px ${sidePad}px`,
                  gap: 12,
                  minHeight: 56,
                  cursor: "pointer",
                  borderBottom: "1px solid #f3f4f6",
                  background: isExpanded ? "#fafafc" : "#fff",
                }}
              >
                <GroupRowContent
                  group={group}
                  confirmDeleteId={confirmDeleteId}
                  editingId={editingId}
                  editName={editName}
                  editInputRef={editInputRef}
                  isExpanded={isExpanded}
                  onDeleteRequest={() => setConfirmDeleteId(group.id)}
                  onConfirmDelete={() => {
                    actions.destroyGroup(group.id);
                    setConfirmDeleteId(null);
                  }}
                  onCancelDelete={() => setConfirmDeleteId(null)}
                  onStartEdit={startEdit}
                  onEditChange={(e) => setEditName(e.target.value)}
                  onEditBlur={commitEdit}
                  onEditKeyDown={handleEditKeyDown}
                />
              </div>

              {isExpanded && (
                <div
                  style={{
                    background: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                    padding: "4px 0",
                  }}
                >
                  {members.length === 0 ? (
                    <div style={{ padding: `12px ${sidePad}px`, color: "#9ca3af", fontSize: 13 }}>
                      No entries in this group yet.
                    </div>
                  ) : (
                    members.map((m) => {
                      const profilePath = memberProfilePath(m.type, m.slug);
                      // Fallback for the window before a saved story idea's title has been hydrated.
                      const memberName = m.name || (m.type === "Enrich::PublicPitch" ? "Story idea" : "Unknown");
                      const authorByline = m.type === "Enrich::PublicPitch" && m.authorName ? m.authorName : null;
                      const authorPath = authorByline
                        ? prProfileUrl({ slug: m.authorSlug, migrate_id: m.authorMigrateId })
                        : null;
                      const nameStyle = {
                        fontSize: 13,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                      };
                      return (
                        <div
                          key={m.membershipId}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: `8px ${sidePad}px` }}
                        >
                          <MemberAvatar name={m.name} imageUrl={m.imageUrl} type={m.type} />
                          {profilePath ? (
                            <a
                              href={profilePath}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary-60"
                              style={{ ...nameStyle, textDecoration: "none" }}
                            >
                              {memberName}
                            </a>
                          ) : (
                            <span style={nameStyle}>
                              {memberName}
                              {authorByline && (
                                <>
                                  <br />
                                  {authorPath ? (
                                    <a
                                      href={authorPath}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-muted"
                                      style={{ fontSize: "0.9em", textDecoration: "none" }}
                                    >
                                      by {authorByline}
                                    </a>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: "0.9em" }}>
                                      by {authorByline}
                                    </span>
                                  )}
                                </>
                              )}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: 11,
                              color: "#6b7280",
                              background: "#e5e7eb",
                              borderRadius: 4,
                              padding: "2px 6px",
                              flexShrink: 0,
                            }}
                          >
                            {SOURCE_TYPE_LABEL[m.type] || m.type}
                          </span>
                          <button
                            type="button"
                            // A membership added moments ago still carries its local id, which
                            // addresses no record on the server. It becomes removable as soon as
                            // the add resolves.
                            disabled={isOptimisticId(m.membershipId)}
                            onClick={(e) => {
                              e.stopPropagation();
                              actions.removeFromGroup(group.id, m.membershipId, m.type, m.sourceableId);
                            }}
                            title="Remove from group"
                            className="text-destructive-80"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: isOptimisticId(m.membershipId) ? "default" : "pointer",
                              opacity: isOptimisticId(m.membershipId) ? 0.4 : 1,
                              padding: 0,
                              flexShrink: 0,
                              lineHeight: 0,
                            }}
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4h6v2" />
                            </svg>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (isMobile) {
    return createPortal(
      <>
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,20,36,0.4)", zIndex: 1050 }}
          onClick={onClose}
          aria-hidden="true"
        />
        {dialog}
      </>,
      document.body,
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label="Close My Groups"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          background: "rgba(0,0,0,0.3)",
          border: "none",
          cursor: "default",
          padding: 0,
        }}
      />
      {dialog}
    </>
  );
}

MyGroupsPanel.propTypes = { onClose: PropTypes.func.isRequired };

function SourceDirectoryInner({
  activeTab: serverActiveTab,
  directoryHeading,
  tabs,
  tabTitles,
  // V1-COMPAT: optional V2 tab key -> host path map, so a host page can serve these tabs at its
  // own paths; threaded into tabFromPathname/tabPath below and into TabBar.
  tabPathMap,
  algoliaAppId,
  algoliaSearchKey,
  expertsIndexName,
  companiesIndexName,
  prProfilesIndexName,
  productsIndexName,
  storyIdeasIndexName,
  mediaProfilesIndexName,
  mediaOutletsIndexName,
}) {
  const getCurrentTab = () => tabFromPathname(window.location.pathname, serverActiveTab, tabPathMap);
  const [activeTab, setActiveTab] = useState(getCurrentTab);
  const [mounted, setMounted] = useState(() => new Set([getCurrentTab()]));
  // Frozen at first render: the page's real pathname before any client-side tab switch. A tab switch
  // rewrites window.location.pathname before that tab's search component mounts, so the live pathname
  // would always match the new tab; this frozen value lets each search component tell whether a bare
  // location_name deep link actually arrived for it.
  const [initialPathname] = useState(() => window.location.pathname);
  const [viewMode, setViewMode] = useState("grid");
  const [showFiltersPerTab, setShowFiltersPerTab] = useState(() => {
    const keys = Array.from(new URL(window.location.href).searchParams.keys());
    const hasRefinements = (indexName) =>
      keys.some((k) => k.startsWith(`${indexName}[refinementList]`) || k.startsWith(`${indexName}[toggle]`));
    const initial = {};
    if (hasRefinements(expertsIndexName)) initial.experts = true;
    if (hasRefinements(companiesIndexName)) initial.companies = true;
    if (hasRefinements(prProfilesIndexName)) initial.pr_profiles = true;
    if (productsIndexName && hasRefinements(productsIndexName)) initial.products = true;
    // Legacy entry params (profile-page tag chips and old inbound links, e.g. ?hashtag=...) are
    // rewritten into the refinement keys above by the tab component AFTER mount, so at init time
    // only the raw param is in the URL. Without this, a visitor arriving from a chip lands on a
    // filtered result list with the filter panel closed — nothing on screen says a filter is
    // active, which reads as a broken or empty directory.
    if (["hashtag", "title", "workplace_name"].some((k) => keys.includes(k))) initial.pr_profiles = true;
    if (["companyName", "gender", "title"].some((k) => keys.includes(k))) initial.experts = true;
    // location_name/location_radius are bare params, so the pathname is the only thing saying which
    // tab's geo filter they belong to — attribute them to whichever tab the current path names.
    if (keys.includes("location_name")) initial[getCurrentTab()] = true;
    return initial;
  });
  const [showMyGroups, setShowMyGroups] = useState(false);
  const [savedOnly, setSavedOnly] = useState(() => new URL(window.location.href).searchParams.get("saved") === "1");
  const savedOnlyMounted = useRef(false);

  useEffect(() => {
    if (!savedOnlyMounted.current) {
      savedOnlyMounted.current = true;
      return;
    }
    sharedAlgoliaRouter.setParam("saved", savedOnly ? "1" : null);
  }, [savedOnly]);

  const savedGroupsState = useSavedGroupsState();
  const groupCount = savedGroupsState?.groups?.length || 0;

  useEffect(() => {
    // Back/forward across tab changes must switch tabs. This listener coexists with
    // sharedAlgoliaRouter's own popstate listener (which restores per-tab filter state) — both
    // are independent `addEventListener("popstate", ...)` registrations reacting to the same
    // event, so neither needs to know about the other.
    const onPopState = () => {
      const tab = tabFromPathname(window.location.pathname, serverActiveTab, tabPathMap);
      setActiveTab(tab);
      setMounted((prev) => new Set([...prev, tab]));
      applyTabTitle(tabTitles, tab);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [serverActiveTab, tabTitles, tabPathMap]);

  const handleTabChange = (tab) => {
    const url = new URL(window.location.href);
    url.pathname = tabPath(url.pathname, tab, tabPathMap);
    url.hash = ""; // fragments are no longer meaningful; don't carry a stale one forward
    // Drop the bare location_name/location_radius deep-link params so they don't linger pointed at a
    // tab they weren't meant for. Stripping only the live URL isn't durable: sharedAlgoliaRouter
    // caches the last query and a later InstantSearch write re-serializes that cache, restoring the
    // params — so the cache must lose them too.
    url.searchParams.delete("location_name");
    url.searchParams.delete("location_radius");
    sharedAlgoliaRouter.deleteParams(["location_name", "location_radius"]);
    // Reuse the current entry's history.state so Turbolinks (v1) restores this tab on Back rather
    // than only changing the URL; null/no-op without Turbolinks (v2).
    window.history.pushState(window.history.state, "", url.toString());
    setActiveTab(tab);
    setMounted((prev) => new Set([...prev, tab]));
    applyTabTitle(tabTitles, tab);
  };

  const sharedProps = {
    algoliaAppId,
    algoliaSearchKey,
    viewMode,
    savedOnly,
    onToggleSavedOnly: () => setSavedOnly((v) => !v),
    onViewModeChange: setViewMode,
    groupCount,
    onMyGroupsClick: () => setShowMyGroups(true),
    initialPathname,
  };

  const filterPropsFor = (tab) => ({
    showFilters: showFiltersPerTab[tab] ?? false,
    onToggleFilters: () => setShowFiltersPerTab((prev) => ({ ...prev, [tab]: !prev[tab] })),
  });

  return (
    <div>
      <h1 style={{ fontWeight: 700, fontSize: 24, marginBottom: 16 }}>{directoryHeading}</h1>
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} tabPathMap={tabPathMap} />

      {mounted.has("experts") && (
        <div style={{ display: activeTab === "experts" ? "block" : "none" }}>
          <ExpertsSearch {...sharedProps} {...filterPropsFor("experts")} indexName={expertsIndexName} />
        </div>
      )}

      {mounted.has("products") && (
        <div style={{ display: activeTab === "products" ? "block" : "none" }}>
          {productsIndexName ? (
            <ProductSearch {...sharedProps} {...filterPropsFor("products")} indexName={productsIndexName} />
          ) : (
            <div className="mt-4 text-muted">Products loading…</div>
          )}
        </div>
      )}

      {mounted.has("pr_profiles") && (
        <div style={{ display: activeTab === "pr_profiles" ? "block" : "none" }}>
          <PrProfiles {...sharedProps} {...filterPropsFor("pr_profiles")} indexName={prProfilesIndexName} />
        </div>
      )}

      {mounted.has("companies") && (
        <div style={{ display: activeTab === "companies" ? "block" : "none" }}>
          <CompaniesSearch {...sharedProps} {...filterPropsFor("companies")} indexName={companiesIndexName} />
        </div>
      )}

      {mounted.has("story_ideas") && (
        <div style={{ display: activeTab === "story_ideas" ? "block" : "none" }}>
          {storyIdeasIndexName ? (
            <StoryIdeasSearch {...sharedProps} {...filterPropsFor("story_ideas")} indexName={storyIdeasIndexName} />
          ) : (
            <div className="mt-4 text-muted">Story ideas loading…</div>
          )}
        </div>
      )}

      {mounted.has("media_profiles") && (
        <div style={{ display: activeTab === "media_profiles" ? "block" : "none" }}>
          {mediaProfilesIndexName ? (
            <MediaProfilesSearch
              {...sharedProps}
              {...filterPropsFor("media_profiles")}
              indexName={mediaProfilesIndexName}
            />
          ) : (
            <div className="mt-4 text-muted">Media loading…</div>
          )}
        </div>
      )}

      {mounted.has("media_outlets") && (
        <div style={{ display: activeTab === "media_outlets" ? "block" : "none" }}>
          {mediaOutletsIndexName ? (
            <MediaOutletsSearch
              {...sharedProps}
              {...filterPropsFor("media_outlets")}
              indexName={mediaOutletsIndexName}
            />
          ) : (
            <div className="mt-4 text-muted">Media outlets loading…</div>
          )}
        </div>
      )}

      {showMyGroups && <MyGroupsPanel onClose={() => setShowMyGroups(false)} />}
    </div>
  );
}

SourceDirectoryInner.propTypes = {
  activeTab: PropTypes.string,
  directoryHeading: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  tabTitles: PropTypes.objectOf(PropTypes.string),
  // V1-COMPAT: optional V2 tab key -> host path map (see tabRouting.js).
  tabPathMap: PropTypes.objectOf(PropTypes.string),
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  expertsIndexName: PropTypes.string.isRequired,
  companiesIndexName: PropTypes.string.isRequired,
  prProfilesIndexName: PropTypes.string.isRequired,
  productsIndexName: PropTypes.string,
  storyIdeasIndexName: PropTypes.string,
  mediaProfilesIndexName: PropTypes.string,
  mediaOutletsIndexName: PropTypes.string,
};

function SourceDirectory(props) {
  return (
    <SavedGroupsProvider>
      <LastMessagesProvider>
        <SourceDirectoryInner {...props} />
      </LastMessagesProvider>
    </SavedGroupsProvider>
  );
}

SourceDirectory.propTypes = {
  activeTab: PropTypes.string,
  directoryHeading: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  tabTitles: PropTypes.objectOf(PropTypes.string),
  // V1-COMPAT: optional V2 tab key -> host path map (see tabRouting.js).
  tabPathMap: PropTypes.objectOf(PropTypes.string),
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  expertsIndexName: PropTypes.string.isRequired,
  companiesIndexName: PropTypes.string.isRequired,
  prProfilesIndexName: PropTypes.string.isRequired,
  productsIndexName: PropTypes.string,
  storyIdeasIndexName: PropTypes.string,
  mediaProfilesIndexName: PropTypes.string,
  mediaOutletsIndexName: PropTypes.string,
};

export default SourceDirectory;
